import { useCallback, useEffect, useState } from 'react';
import { texto } from './i18n';

/**
 * Cliente de la API interna.
 *
 * Delgado a propósito: `fetch` más manejo de errores tipado. Un cliente generado o una
 * librería de datos sería más peso en el bundle del que justifica un panel con una decena
 * de endpoints, y el bundle se sirve desde el plan gratuito.
 */

export type ApiError = {
  code: string;
  message: string;
  retryable?: boolean;
  details?: Record<string, unknown>;
};

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly payload: ApiError,
  ) {
    super(payload.message);
    this.name = 'ApiRequestError';
  }
}

/**
 * Quién quiere enterarse de que una petición salió mal.
 *
 * Lo usa el sistema de avisos para mostrar **todos** los errores sin que cada pantalla tenga
 * que acordarse de hacerlo. Es el mismo razonamiento que el del aislamiento en el servidor: un
 * control que depende de que cada llamada lo aplique se rompe con la próxima que alguien
 * escriba, y no hay forma de notarlo — nada falla, sólo deja de avisar.
 *
 * Vive acá y no en un módulo de React porque el punto único es este: por `request` pasan todas
 * las peticiones del panel, incluidas las que todavía no existen.
 */
type Oyente = (error: ApiError, contexto: { metodo: string; path: string }) => void;
const oyentes = new Set<Oyente>();

/** Registra un oyente y devuelve cómo darlo de baja. */
export function alSalirMal(oyente: Oyente): () => void {
  oyentes.add(oyente);
  return () => oyentes.delete(oyente);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      /*
        Un `FormData` trae su propio `content-type`, y hay que dejárselo poner.

        Incluye el `boundary` que separa las partes, y ese valor lo genera el navegador al
        serializar el cuerpo. Forzando `application/json` acá, el servidor recibe un multipart
        cuyo separador no está declarado: lo lee como un cuerpo vacío y contesta que falta el
        archivo, que es exactamente el mensaje que no lleva a la causa.
      */
      ...(init.body instanceof FormData ? {} : { 'content-type': 'application/json' }),
      ...(init.headers ?? {}),
    },
    // Las cookies de sesión son HttpOnly, así que hay que pedirlas explícitamente.
    credentials: 'same-origin',
  });

  const text = await response.text();
  const body = text.length > 0 ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const payload =
      body && typeof body === 'object' && 'error' in body
        ? (body as { error: ApiError }).error
        : { code: 'UNKNOWN', message: `Error ${response.status}` };

    const metodo = init.method ?? 'GET';

    /**
     * Se avisa de lo que modifica; de lo que sólo lee, no.
     *
     * Un GET que falla ya tiene su lugar: la pantalla que no pudo cargar lo muestra en su sitio,
     * con su botón de reintentar. Duplicarlo en un aviso flotante sería decir dos veces lo
     * mismo — y como varias pantallas consultan cada tres segundos, un servidor caído llenaría
     * la esquina de avisos idénticos.
     *
     * El 401 tampoco: la sesión venció, y de eso se encarga la pantalla de acceso.
     */
    if (metodo !== 'GET' && response.status !== 401) {
      for (const oyente of oyentes) oyente(payload, { metodo, path });
    }

    throw new ApiRequestError(response.status, payload);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  /** Sube archivos. El navegador arma el `content-type` con su separador; ver `request`. */
  postForm: <T>(path: string, body: FormData) => request<T>(path, { method: 'POST', body }),
  /**
   * Sube UN archivo como cuerpo crudo, sin envoltorio multipart.
   *
   * La ruta de la referencia de cuerpo lee `c.req.arrayBuffer()` y le mira los primeros bytes
   * para saber si es PNG o JPEG. Un `FormData` le llegaría con el separador y las cabeceras de
   * cada parte adentro del cuerpo, así que la comprobación de formato fallaría sobre basura.
   */
  postArchivo: <T>(path: string, archivo: File) =>
    request<T>(path, {
      method: 'POST',
      body: archivo,
      headers: { 'content-type': archivo.type || 'application/octet-stream' },
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// ── Credenciales ──────────────────────────────────────────────────────────────

/** Costo de cada perfil, calculado por el servidor con el estimador real. */
export type ProfileEstimate = {
  profile: string;
  neuronsPerCreator: number;
  /** Lo que cuesta una toma publicable. Es la unidad con la que se comparan los perfiles. */
  neuronsPerImage: number;
  usdPerImage: number;
  breakdown: {
    candidates: number;
    identitySheet: number;
    finalImages: number;
    text: number;
    total: number;
  };
  creatorsPerDay: number;
  /** Lo que costaría en dinero si el consumo excediera el tramo gratuito del día. */
  usdPerCreator: number;
  /** `false` si el modelo no acepta referencias: sin ellas no hay cadena de identidad. */
  supportsIdentityChain: boolean;
  /** Quién factura. Cambia qué significa el número de neuronas y de dónde sale el cobro. */
  provider: 'workers-ai' | 'gemini';
};

export type CredentialKind =
  | 'cloudflare_account'
  | 'email_base'
  | 'email_domain'
  | 'instagram_app'
  | 'facebook_app'
  | 'fanvue_app'
  | 'runware'
  | 'email_routing'
  | 'telegram_bot'
  | 'civitai';

/** Una capacidad comprobada contra la API del proveedor. */
export type CredentialCapability = {
  key: string;
  label: string;
  ok: boolean;
  detail: string;
  /**
   * `false` cuando la comprobación no pudo hacerse, y entonces `ok` no afirma nada.
   *
   * Ausente en los chequeos guardados antes de que esto existiera: ésos sí se hicieron. Ver
   * `estadoDe` en `pages/Credentials.tsx`.
   */
  verified?: boolean;
};

export type CredentialCheck = {
  ok: boolean;
  capabilities: CredentialCapability[];
  /** Qué hacer si algo falló, en texto llano. `null` cuando está todo bien. */
  remedy: string | null;
  checkedAt: string;
};

/**
 * Una credencial tal como la ve la interfaz.
 *
 * `secretHint` son los últimos cuatro caracteres. El secreto completo **no existe** en el
 * cliente: la API no lo devuelve por ninguna ruta.
 */
export type CredentialView = {
  id: string;
  kind: CredentialKind;
  label: string;
  name: string;
  secretHint: string;
  metadata: Record<string, string>;
  isActive: boolean;
  lastCheckAt: string | null;
  lastCheck: CredentialCheck | null;
  createdAt: string;
  updatedAt: string;
};

export type CredentialsData = {
  credentials: CredentialView[];
  activeAccountLabel: string;
  /** `false` si falta TOKEN_ENCRYPTION_KEY: sin ella no se puede guardar nada. */
  encryptionAvailable: boolean;
  environmentFallback: Record<string, boolean>;
  /** Casilla principal en uso. No es secreta: es la dirección propia de quien administra. */
  emailBase: string | null;
  /** `true` si el panel todavía se abre con la contraseña que deja `make secrets`. */
  usingDefaultPassword: boolean;
  /**
   * Si quien mira administra la app de Meta, que es una sola para todo el panel.
   *
   * A los demás no se les muestra esa tarjeta: su fila propia no existe, así que diría «Sin
   * configurar» sobre algo que está puesto y les funciona.
   */
  administraAppDeMeta?: boolean;
};

export type AsyncState<T> = {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
};

/**
 * Carga datos de un endpoint.
 *
 * `pollMs` sirve para seguir un workflow en curso. La generación de una influencer tarda
 * minutos, y sin sondeo el administrador vería una pantalla congelada sin saber si el
 * proceso avanza o murió.
 */
export function useApi<T>(path: string | null, pollMs?: number): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const result = await api.get<T>(path);
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(
            caught instanceof ApiRequestError
              ? caught.payload
              : { code: 'NETWORK', message: texto('api.sinConexion') },
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    if (pollMs === undefined) {
      return () => {
        cancelled = true;
      };
    }

    /*
      El sondeo se para cuando la pestaña no se ve.

      ── El defecto ──────────────────────────────────────────────────────────────
      Cloudflare avisó: «75% of the daily D1 free tier limit of 5.000.000 rows_read», con una
      base de quince mil filas. La causa principal era una consulta que escaneaba la tabla, y
      está arreglada con un índice. La causa de fondo es ésta: cuatro pantallas sondeando entre
      cada quince y cada sesenta segundos, para siempre, aunque el panel esté minimizado o en una
      pestaña de atrás.

      Con una jornada de ocho horas son ~5.300 peticiones por pestaña abierta, y la mayoría
      ocurre mientras nadie está mirando. Cada una lee filas de D1, así que el gasto es real
      aunque el resultado no lo vea nadie.

      ── Por qué recargar al volver, y no sólo reanudar ──────────────────────────
      Porque lo que se vio al minimizar quedó viejo. Reanudar el temporizador sin pedir de nuevo
      deja hasta un intervalo entero de datos vencidos en pantalla, que es justo cuando alguien
      vuelve a mirar. Se pide una vez al volver y después sigue el ritmo normal.

      ── Por qué no se toca `pollMs` ─────────────────────────────────────────────
      Porque el intervalo dice cada cuánto cambia el dato, y eso no cambió: lo que cambió es que
      antes se pagaba por mirar una pantalla que nadie tenía delante.
    */
    let timer: ReturnType<typeof setInterval> | null = null;

    const arrancar = (): void => {
      if (timer === null) timer = setInterval(() => void load(), pollMs);
    };
    const parar = (): void => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const alCambiarVisibilidad = (): void => {
      if (document.visibilityState === 'visible') {
        void load();
        arrancar();
      } else {
        parar();
      }
    };

    if (document.visibilityState === 'visible') arrancar();
    document.addEventListener('visibilitychange', alCambiarVisibilidad);

    return () => {
      cancelled = true;
      parar();
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
    };
  }, [path, pollMs, nonce]);

  return { data, error, loading, reload };
}

// ── Tipos de las respuestas de la API ──────────────────────────────────────────

/** Lo gastado, en dinero. Un presupuesto sin contador es una intención, no un control. */
export type SpendSummary = {
  /** Gasto real: sólo el excedente del tramo gratuito, más los proveedores que no lo tienen. */
  monthUsd: number;
  /** Lo que costaría sin tramo gratuito. Explica de dónde sale la diferencia. */
  monthWithoutFreeTierUsd: number;
  dayUsd: number;
  dayCredits: number;
  monthlyBudgetUsd: number;
  /** La parte que cobra un proveedor desde la primera llamada, sin tramo gratuito. */
  billedFromFirstCallUsd: number;
};

/**
 * La cuota del día, con las dos billeteras separadas.
 *
 * Los campos sueltos —`limit`, `used`, `byOperation`— son los de la **asignación gratuita** de
 * Cloudflare. `pago` es la otra: proveedores que facturan. No se suman porque no son la misma
 * cosa; sumarlas producía «169.877 de 10.000» con el cupo gratuito por la mitad.
 */
export type QuotaSnapshot = {
  dayUtc: string;
  /** `null` cuando no hay tope diario. Es una ausencia elegida, no un dato que falta. */
  limit: number | null;
  used: number;
  remaining: number | null;
  percentUsed: number;
  resetsAt: string;
  /** El proveedor reportó la cuota agotada, por encima de lo que estima el ledger. */
  providerExhausted: boolean;
  byOperation: { operation: string; used: number; limit: number | null }[];
  pago: {
    /** Créditos equivalentes. Sirven para comparar; lo que se paga son los dólares. */
    used: number;
    limit: number | null;
    usd: number;
    byOperation: { operation: string; used: number }[];
  };
};

export type DashboardData = {
  statusCounts: Record<string, number>;
  activeCreators: number;
  waitingCreators: number;
  pendingTasks: number;
  /**
   * Lo programado, con si va a salir de verdad.
   *
   * `publicable` es `false` cuando el ítem sigue en borrador: el cron exige `APPROVED` o
   * `SCHEDULED`, así que esas programaciones se quedan pendientes para siempre.
   */
  upcoming: {
    id: string;
    contentItemId: string;
    platform: string;
    scheduledFor: string;
    itemStatus: string;
    publicable: boolean;
  }[];
  errors: {
    platform: string;
    errorCode: string;
    errorMessage: string;
    count: number;
    lastSeenAt: string;
  }[];
  deferred: { id: string; operation: string; reason: string; runAfter: string }[];
  quota: QuotaSnapshot;
  costEstimate: {
    profile: string;
    neuronsPerCreator: number;
    /** Lo que cuesta una toma. El total por influencer es para la capacidad del día. */
    neuronsPerImage: number;
    breakdown: {
      candidates: number;
      identitySheet: number;
      finalImages: number;
      text: number;
      total: number;
    };
    remainingCreatorsToday: number;
  };
  settings: {
    zeroSpendMode: boolean;
    imageQualityProfile: string;
    videoProvider: string;
    dailyNeuronLimit: number;
    monthlyBudgetUsd: number;
  };
  providers: { name: string; available: boolean; canIncurCost: boolean }[];
  /**
   * Proveedores de imagen que este dueño tiene activos.
   *
   * Lo usa el formulario de creación para no ofrecer niveles de piel que no puede generar.
   */
  configProblems: { key: string; message: string; fatal: boolean }[];
};

export type CreatorSummary = {
  id: string;
  publicName: string;
  slug: string;
  status: string;
  niche: string[];
  canonicalReferenceAssetId: string | null;
  /** La foto de cuerpo entero que se manda de referencia en las tomas lejanas, si eligió una. */
  referenciaDeCuerpoAssetId: string | null;
  pendingTaskCount: number;
  /** URL firmada de su imagen representativa, o `null` si todavía no generó ninguna. */
  avatarUrl: string | null;
  /** Perfiles públicos ya registrados. Vacío mientras no exista ninguna cuenta. */
  profiles: { platform: string; username: string; url: string }[];
  /**
   * Seguidores sumados de todas sus redes, o `null` si nunca se midió.
   *
   * Es lo que ordena el listado principal, y por eso viaja resuelto desde el servidor: un orden
   * que cada pantalla calcula por su cuenta es un orden que se contradice entre pantallas.
   *
   * `null` no es `0`. Una influencer recién creada no tiene a nadie porque todavía no empezó;
   * una medida en cero no tiene a nadie porque no funcionó.
   */
  seguidores: number | null;
  createdAt: string;
};

/**
 * Realce de belleza al regenerar. Es el contrato con la API, no una regla duplicada.
 *
 * La web no importa de `@vcos/domain` a propósito: sólo habla con la API. Eso vale para las
 * reglas —que las calcula el servidor y viajan resueltas— pero un tipo de tres valores que la
 * web tiene que saber para pintar dos botones es la forma del pedido, y vive donde viven las
 * otras formas de pedido. Si el servidor agregara un modo, esto no se entera: por eso el
 * servidor valida lo que llega en vez de confiar.
 */
export type BeautyMode = 'off' | 'normal' | 'high';

export type AssetView = {
  id: string;
  kind: string;
  status: string;
  /**
   * Dónde puede publicarse: `sfw` en cualquier plataforma, `nsfw` sólo en Fanvue.
   *
   * El servidor la hace cumplir al publicar, así que acá sólo decide qué panel la muestra. Una
   * comprobación en la pantalla no habría servido de nada: lo que publica es el cron, con el
   * panel cerrado.
   */
  clasificacion: 'sfw' | 'nsfw';
  url: string;
  width: number;
  height: number;
  /** Tamaño del archivo. Se usa para decir cuánto espacio libera eliminarlo. */
  byteSize: number;
  /** Parecido con la canónica. No significa nada sin `identityMethod`. */
  consistencyScore: number | null;
  /** `sface-cosine` si se midió mirando la imagen; nulo si nunca se midió. */
  identityMethod: string | null;
  identityScoredAt: string | null;
  /**
   * Qué pasaría al animar esta toma. Nulo si no se conocen sus medidas.
   *
   * `cropped` es la fracción que quedaría fuera de cuadro, y lo que queda fuera el modelo lo
   * inventa: por eso se avisa antes de gastar y no después de mirar el clip.
   */
  video: { aspect: string; cropped: number } | null;
  /**
   * La versión en alta de esta toma, si alguien la pidió. `null` en casi todas.
   *
   * La URL ya viene firmada por la ficha, igual que `url`: el almacén nunca es público (§18).
   */
  escalado: { width: number; height: number; byteSize: number; url: string } | null;
  /** La canción de este clip, si tiene. `null` en las fotos y en los clips mudos. */
  musica: { pistaId: string | null; volumen: number; byteSize: number } | null;
  provenance: {
    provider: string;
    model: string;
    prompt: string;
    seed: number | null;
    steps: number | null;
    estimatedNeurons: number;
    generatedAt: string;
    /**
     * Los parámetros con los que se generó. El servidor los devuelve enteros; acá se declaran
     * los que la pantalla usa, y el resto queda accesible sin tipar de más.
     *
     * `contentBatchId` y `contentBatchSize` son los que permiten mostrar «2 de 4 generadas»
     * sin pedirle nada nuevo al servidor.
     */
    parameters: Record<string, unknown>;
  };
};

export type TaskView = {
  id: string;
  creatorId: string;
  platform: string;
  type: string;
  status: string;
  instructions: string;
  rationale: string;
  dataToUse: string[];
  dataToNeverShare: string[];
  externalUrl?: string;
  blockedReason?: string;
  completedAt?: string;
};

export type ContentView = {
  id: string;
  pillar: string;
  topic: string;
  status: string;
  aspectRatio: string;
  scheduledFor: string | null;
  assetId: string | null;
  /**
   * De qué clase es el archivo que lleva, para no mezclar las dos pestañas.
   *
   * Lo resuelve el servidor cruzando la publicación con su asset: son dos listas distintas y
   * cruzarlas en cada pantalla es trabajo repetido que alguna se olvida de hacer. Alguna se lo
   * olvidó: las publicaciones de Fanvue aparecieron dentro de las de contenido apto.
   */
  clasificacion: 'sfw' | 'nsfw';
  variants: {
    id: string;
    platform: string;
    /**
     * Quién va a poder verla. En Telegram decide si sale difuminada tras las Stars o abierta.
     *
     * El servidor ya lo devolvía; faltaba acá, así que la pantalla no podía decir si una toma
     * iba de pago antes de publicarla — el dato existía y no llegaba.
     */
    audience: 'public' | 'subscribers';
    caption: string;
    hashtags: string[];
    altText: string;
    disclosureLevel: string;
  }[];
};

export type CreatorDetailData = {
  /**
   * El LoRA de forma que le toca a esta influencer, ya resuelto por el servidor.
   *
   * Viene resuelto y no como tabla: la pantalla no tiene por qué repetir la regla de cruzar el
   * tipo de cuerpo con el nivel de piel. `bodyType` y `skinLevel` viajan para saber qué celda se
   * está editando al guardar.
   */
  loraDeCuerpo?: {
    bodyType: string;
    skinLevel: string;
    actual: { archivo: string; peso: number } | null;
    porDefecto: { archivo: string; pesos: Record<string, number> } | null;
    /** Todo lo configurado, para fusionar al editar una celda en vez de reemplazar el mapa. */
    configurado: Record<string, { archivo: string; pesos: Record<string, number> } | null> | null;
  } | null;
  /** Clips generándose ahora mismo. El trabajo vive en el servidor, no en esta pestaña. */
  videoJobs: { id: string; sourceAssetId: string; interactionId: string }[];
  /**
   * Cuántas veces se vendió cada publicación en Telegram, indexado por `contentItemId`.
   *
   * Es lo que la tarjeta de cada publicación necesita para decir si ésa vendió. El total de la
   * influencer no viaja acá: vive en Estadísticas, que es donde se compara con las demás.
   */
  ventasPorPublicacion?: Record<string, { ventas: number; stars: number }>;
  /**
   * Con qué se anima esta influencer. Lo decide el servidor a partir de su nivel de piel.
   *
   * `followsSourceAspect` en falso significa que una foto de otra proporción se extiende antes,
   * y eso cuesta una imagen más. Con fal no pasa.
   */
  videoPlan: {
    provider: string;
    seconds: number;
    usdPerSecond: number;
    followsSourceAspect: boolean;
    available: boolean;
  };
  creator: CreatorSummary & {
    /** Cuándo se publicó su página pública, o `null` si todavía no. */
    proxyPublishedAt: string | null;
    disclosureText: string | null;
    fictionalAge: number | null;
    countryNarrative: string | null;
    updatedAt: string;
    error: { code: string; message: string; retryable: boolean } | null;
    /**
     * `human` o `character`. Un personaje no humano recorre la misma cadena pero no se le
     * puede medir la compatibilidad: el detector busca caras humanas.
     */
    kind: 'human' | 'character' | 'external';
    /** Las decisiones creativas: registro, nivel de piel, complexión, tono, entornos y luz. */
    creativeProfile: {
      register: string;
      skinLevel: string;
      bodyType: string;
      feedTone: string;
      sceneSetting: string;
      /**
       * De dónde viene la luz.
       *
       * No es opcional aunque el JSON guardado de una influencer vieja no lo traiga: lo que
       * llega acá pasó por `CreativeProfileSchema`, que le pone `frontal`. Declararlo opcional
       * dejaría el desplegable sin valor seleccionado —un `select` sin `value` es no
       * controlado— por un caso que el servidor no puede producir.
       */
      iluminacion: string;
      /** Qué tan bien tomada está la foto. Aparte de la luz: la misma cámara mala de día y de noche. */
      calidad: string;
    };
  };
  bible: Record<string, unknown> | null;
  bibleVersion: number;
  bibleHistory: { version: number; reason: string; createdAt: string }[];
  assets: AssetView[];
  content: ContentView[];
  tasks: TaskView[];
  accounts: {
    platform: string;
    username: string | null;
    /** El identificador en la plataforma. En Facebook, el de la Página. */
    externalUserId: string | null;
    status: string;
    suggestedUsernames: string[];
    emailAddress: string | null;
    emailDeliversTo: string | null;
    /** `true` si hay contraseña guardada. El valor nunca viaja en el listado. */
    hasPassword: boolean;
    passwordUpdatedAt: string | null;
  }[];
  connections: { platform: string; status: string; scopes: string[] }[];
  /** Direcciones de Gmail candidatas, derivadas del nombre público por el servidor. */
  suggestedEmails: string[];
  /**
   * La dirección que le toca a esta influencer: de dominio propio si hay uno, y si no, alias
   * sobre la casilla principal. `null` si no hay ninguna de las dos configuradas.
   *
   * El dominio gana porque un alias con «+» deja las cuentas enlazadas entre sí, y una
   * dirección de dominio no.
   */
  /** El dominio propio, si está configurado. Decide si la influencer tiene pestaña de correo. */
  dominioDeCorreo: string | null;
  direccionDeCorreo: string | null;
  workflowRuns: {
    id: string;
    status: string;
    currentStep: string | null;
    completedSteps: string[];
    estimatedNeurons: number;
    startedAt: string;
    error: { code: string; message: string; retryable: boolean } | null;
  }[];
  posts: {
    id: string;
    /** A qué tarjeta del calendario pertenece. Es lo que permite saber qué ya salió. */
    contentItemId: string;
    platform: string;
    status: string;
    permalink: string | null;
    publishedAt: string | null;
    metrics: Record<string, number | null> | null;
  }[];
  audit: { action: string; actorType: string; createdAt: string }[];
};
